/* ================================================================
   Puzzle Match — 7-step card flow with live availability counts.
   Fetches filter facet counts between steps to hide 0-result options
   and show product counts. Falls back gracefully on network errors.
   ================================================================ */
(function() {
  'use strict';

  var TOTAL_STEPS = 7;
  var root = document.querySelector('.pgpm');
  if (!root) return;

  /* ── State ── */
  var currentStep = 1;
  var choices = {
    purpose: null,
    subject: null,
    artstyle: null,
    pieces: null,
    budget: null,
    brands: []
  };
  var lastFacets = null; /* cached facet data from most recent fetch */

  /* ── DOM refs ── */
  var cards = root.querySelectorAll('.pgpm-card');
  var dots = root.querySelectorAll('.pgpm-progress__dot');
  var ctaLink = root.querySelector('#pgpm-cta');
  var summaryEl = root.querySelector('#pgpm-summary');
  var previewsEl = root.querySelector('#pgpm-previews');
  var titleEl = root.querySelector('.pgpm-results__title');
  var relaxedEl = root.querySelector('#pgpm-relaxed');
  var nextBtn = root.querySelector('.pgpm-next');

  /* ══════════════════════════════════════════════════════════════
     FILTER MAPPINGS
     ══════════════════════════════════════════════════════════════ */

  var purposeFilters = {
    relaxing: ['filter.p.m.puzzle.difficulty=1', 'filter.p.m.puzzle.difficulty=2', 'filter.p.m.puzzle.difficulty=3'],
    challenge: ['filter.p.m.puzzle.difficulty=4', 'filter.p.m.puzzle.difficulty=5'],
    family: ['filter.p.m.puzzle.difficulty=1', 'filter.p.m.puzzle.difficulty=2', 'filter.p.m.puzzle.difficulty=3'],
    gift: []
  };

  function mf(ns, val) { return 'filter.p.m.' + ns + '=' + encodeURIComponent(val); }

  var subjectFilters = {
    animals: [mf('puzzle.subject', 'Animals & Wildlife')],
    countryside: [mf('puzzle.subject', 'Nature & Landscape')],
    towns: [mf('puzzle.subject', 'Towns & Cities')],
    art: [mf('puzzle.subject', 'Art & Fine Art')],
    nostalgia: [mf('puzzle.subject', 'Nostalgic & Retro')],
    fantasy: [mf('puzzle.subject', 'Fantasy & Mythology')],
    flowers: [mf('puzzle.subject', 'Flowers & Gardens')],
    maps: [mf('puzzle.subject', 'Maps & Geography')],
    buildings: [mf('puzzle.subject', 'Buildings & Architecture')],
    people: [mf('puzzle.subject', 'People & Characters')],
    humour: [mf('puzzle.subject', 'Humour & Cartoon')],
    food: [mf('puzzle.subject', 'Food & Drink')],
    vehicles: [mf('puzzle.subject', 'Vehicles & Transport')],
    collage: [mf('puzzle.subject', 'Collage & Montage')],
    seasonal: [mf('puzzle.subject', 'Seasonal & Holiday')],
    pattern: [mf('puzzle.subject', 'Pattern & Geometric')],
    all: null
  };

  var artstyleFilters = {
    photograph: [mf('puzzle.art_style', 'Photograph')],
    oil: [mf('puzzle.art_style', 'Oil/Acrylic Painting')],
    oilacrylic: [mf('puzzle.art_style', 'Oil/Acrylic Painting')],
    digital: [mf('puzzle.art_style', 'Digital Illustration')],
    cartoon: [mf('puzzle.art_style', 'Cartoon/Whimsical')],
    watercolour: [mf('puzzle.art_style', 'Watercolour')],
    vintage: [mf('puzzle.art_style', 'Vintage/Retro Illustration')],
    folk: [mf('puzzle.art_style', 'Folk Art / Naive Art')],
    mixed: [mf('puzzle.art_style', 'Mixed Media')],
    sketch: [mf('puzzle.art_style', 'Sketch/Line Drawing')],
    '3d': [mf('puzzle.art_style', '3D Render')],
    pop: [mf('puzzle.art_style', 'Pop Art')],
    any: null,
    none: null
  };

  var pieceCollections = {
    under500: '251-399-pieces',
    '500to1000': '500-999-pieces',
    '1000to2000': '1000-piece-puzzles',
    over2000: '2000-pieces'
  };

  var budgetFilters = {
    under10: ['filter.v.price.lte=10'],
    '10to20': ['filter.v.price.gte=10', 'filter.v.price.lte=20'],
    '20to35': ['filter.v.price.gte=20', 'filter.v.price.lte=35'],
    any: null
  };

  /* ── Summary labels ── */
  var purposeLabels = { relaxing: 'something relaxing', challenge: 'a proper challenge', family: 'something for the whole family', gift: 'a gift' };
  var subjectLabels = { animals: 'animals and wildlife', countryside: 'countryside and nature', towns: 'towns and cities', art: 'art and paintings', nostalgia: 'nostalgia and retro', fantasy: 'fantasy and mythology', flowers: 'flowers and gardens', maps: 'maps and travel', buildings: 'buildings and architecture', people: 'people and characters', humour: 'humour and cartoons', food: 'food and drink', vehicles: 'vehicles and transport', collage: 'collage and montage', seasonal: 'seasonal and holiday', pattern: 'patterns and geometry', all: 'everything' };
  var artstyleLabels = { photograph: 'photography', oilacrylic: 'oil and acrylic painting', digital: 'digital illustration', cartoon: 'cartoon and whimsical', watercolour: 'watercolour', vintage: 'vintage and retro', oil: 'oil and acrylic painting', folk: 'folk art', mixed: 'mixed media', sketch: 'sketch and line drawing', '3d': '3D render', pop: 'pop art', any: null, none: null };
  var piecesLabels = { under500: 'under 500 pieces', '500to1000': '500 to 1,000 pieces', '1000to2000': '1,000 to 2,000 pieces', over2000: '2,000+ pieces', any: null };
  var budgetLabels = { under10: 'under \u00a310', '10to20': '\u00a310 to \u00a320', '20to35': '\u00a320 to \u00a335', any: null };

  /* ══════════════════════════════════════════════════════════════
     FACET COUNT SYSTEM — Maps quiz options to Shopify filter facets
     ══════════════════════════════════════════════════════════════ */

  /* Which Shopify filter facet param corresponds to each quiz step's options.
     Steps not listed here don't use facet counts (pieces=collection-based, budget=price-range). */
  var STEP_FACET_PARAM = {
    2: 'filter.p.m.puzzle.subject',
    3: 'filter.p.m.puzzle.art_style',
    6: 'filter.p.vendor'
  };

  /* Map from option data-value → Shopify facet label for lookup.
     Brands use data-value directly (already the vendor name). */
  var FACET_LABEL_MAP = {
    subject: {
      animals: 'Animals & Wildlife', countryside: 'Nature & Landscape',
      towns: 'Towns & Cities', art: 'Art & Fine Art',
      nostalgia: 'Nostalgic & Retro', fantasy: 'Fantasy & Mythology',
      flowers: 'Flowers & Gardens', maps: 'Maps & Geography',
      buildings: 'Buildings & Architecture', people: 'People & Characters',
      humour: 'Humour & Cartoon', food: 'Food & Drink',
      vehicles: 'Vehicles & Transport', collage: 'Collage & Montage',
      seasonal: 'Seasonal & Holiday', pattern: 'Pattern & Geometric'
    },
    artstyle: {
      digital: 'Digital Illustration', cartoon: 'Cartoon/Whimsical',
      oil: 'Oil/Acrylic Painting', oilacrylic: 'Oil/Acrylic Painting',
      watercolour: 'Watercolour', vintage: 'Vintage/Retro Illustration',
      folk: 'Folk Art / Naive Art', mixed: 'Mixed Media',
      photograph: 'Photograph', sketch: 'Sketch/Line Drawing',
      '3d': '3D Render', pop: 'Pop Art'
    }
    /* brands: data-value IS the label, no mapping needed */
  };

  /* Which data-key corresponds to each step (for finding option buttons) */
  var STEP_DATA_KEY = { 2: 'subject', 3: 'artstyle', 6: 'brand' };

  /* ── Reduced motion ── */
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ══════════════════════════════════════════════════════════════
     URL BUILDING
     ══════════════════════════════════════════════════════════════ */

  function buildUrl(dropList) {
    var drop = dropList || [];

    var baseCollection = 'all';
    if (drop.indexOf('pieces') === -1 && choices.pieces && choices.pieces !== 'any' && pieceCollections[choices.pieces]) {
      baseCollection = pieceCollections[choices.pieces];
    }

    var params = [];

    if (drop.indexOf('purpose') === -1 && choices.purpose && purposeFilters[choices.purpose]) {
      purposeFilters[choices.purpose].forEach(function(p) { params.push(p); });
    }
    if (drop.indexOf('subject') === -1 && choices.subject && subjectFilters[choices.subject]) {
      var sf = subjectFilters[choices.subject];
      if (Array.isArray(sf)) sf.forEach(function(p) { params.push(p); });
    }
    if (drop.indexOf('artstyle') === -1 && choices.artstyle && artstyleFilters[choices.artstyle]) {
      var af = artstyleFilters[choices.artstyle];
      if (Array.isArray(af)) af.forEach(function(p) { params.push(p); });
    }
    if (drop.indexOf('brands') === -1 && choices.brands.length > 0 && choices.brands[0] !== 'none') {
      choices.brands.forEach(function(brand) {
        params.push('filter.p.vendor=' + encodeURIComponent(brand));
      });
    }
    if (drop.indexOf('budget') === -1 && choices.budget && budgetFilters[choices.budget]) {
      budgetFilters[choices.budget].forEach(function(p) { params.push(p); });
    }

    params.push('filter.v.availability=1');

    var url = '/collections/' + baseCollection;
    if (params.length > 0) url += '?' + params.join('&');
    return url;
  }

  /* Build summary sentence. When dropped filters are provided, those parts
     get wrapped in <s> tags so the user sees what was removed. Returns HTML. */
  function buildSummary(dropped) {
    var drop = dropped || [];
    var parts = [];

    function wrap(text, key) {
      if (drop.indexOf(key) !== -1) return '<s>' + text + '</s>';
      return text;
    }

    if (choices.purpose) parts.push(wrap('You\'re looking for ' + purposeLabels[choices.purpose], 'purpose'));
    if (choices.subject && choices.subject !== 'all') parts.push(wrap('featuring ' + subjectLabels[choices.subject], 'subject'));
    if (choices.artstyle && artstyleLabels[choices.artstyle]) parts.push(wrap('in ' + artstyleLabels[choices.artstyle] + ' style', 'artstyle'));
    if (choices.pieces && piecesLabels[choices.pieces]) parts.push(wrap(piecesLabels[choices.pieces], 'pieces'));
    if (choices.budget && budgetLabels[choices.budget]) parts.push(wrap(budgetLabels[choices.budget], 'budget'));
    if (choices.brands.length > 0 && choices.brands[0] !== 'none') {
      var brandText = choices.brands.length === 1
        ? 'by ' + choices.brands[0]
        : 'by ' + choices.brands.slice(0, -1).join(', ') + ' or ' + choices.brands[choices.brands.length - 1];
      parts.push(wrap(brandText, 'brands'));
    }
    if (parts.length > 0) {
      var s = parts.join(', ') + '.';
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
    return 'We\'ve matched you with our full puzzle collection.';
  }

  /* ══════════════════════════════════════════════════════════════
     FETCHING — Counts (lightweight) and full page (for results)
     ══════════════════════════════════════════════════════════════ */

  /* Fetch filter facet counts via Section Rendering API (tiny payload) */
  function fetchCounts(url) {
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var countUrl = url + sep + 'sections=pg-puzzle-match-counts';

    return fetch(countUrl)
      .then(function(res) { return res.json(); })
      .then(function(json) {
        var raw = json['pg-puzzle-match-counts'] || '';
        /* Section Rendering wraps in a <div>, extract the JSON inside */
        var match = raw.match(/\{[\s\S]*\}/);
        if (!match) return null;
        return JSON.parse(match[0]);
      })
      .catch(function() { return null; });
  }

  /* Fetch the results count + thumbnails.
   *
   * ⚠ This used to fetch the WHOLE filtered collection page and run it through
   * DOMParser to read one number and four image URLs. Measured live:
   * /collections/1000-piece-puzzles is 1,131,558 bytes; this section endpoint is 7,824
   * — 145x smaller. The full-page path also blocked the main thread parsing a
   * multi-megabyte document, on mobile, at the moment the visitor is about to convert,
   * and tryRelax() fired it again serially when the first filter combination came back
   * empty.
   *
   * The cheap endpoint was already here, eight lines above, in fetchCounts(). It now
   * also emits count and thumbs (see sections/pg-puzzle-match-counts.liquid), so both
   * callers use one request shape. Do not reintroduce the DOMParser path. */
  function fetchCollectionPage(url) {
    var sep = url.indexOf('?') === -1 ? '?' : '&';

    return fetch(url + sep + 'sections=pg-puzzle-match-counts')
      .then(function(res) { return res.json(); })
      .then(function(json) {
        var raw = json['pg-puzzle-match-counts'] || '';
        /* Section Rendering wraps the payload in a <div>; pull the JSON back out. */
        var match = raw.match(/\{[\s\S]*\}/);
        if (!match) return { count: -1, thumbs: [] };

        var data = JSON.parse(match[0]);
        var thumbs = [];
        var seen = {};
        var list = data.thumbs || [];
        for (var i = 0; i < list.length && thumbs.length < 4; i++) {
          var src = list[i];
          if (!src || seen[src]) continue;
          seen[src] = true;
          thumbs.push(src);
        }

        return { count: typeof data.count === 'number' ? data.count : 0, thumbs: thumbs };
      })
      .catch(function() {
        return { count: -1, thumbs: [] };
      });
  }

  /* ══════════════════════════════════════════════════════════════
     APPLY COUNTS — Update option visibility and count badges
     ══════════════════════════════════════════════════════════════ */

  /* Apply facet counts to a step's option buttons.
     Hides options with 0 results, shows count badge on the rest. */
  function applyCounts(step, facetData) {
    var param = STEP_FACET_PARAM[step];
    var dataKey = STEP_DATA_KEY[step];
    if (!param || !dataKey || !facetData) return;

    var filterObj = facetData.filters && facetData.filters[param];
    var values = filterObj && filterObj.values || {};

    var card = root.querySelector('[data-card="' + step + '"]');
    if (!card) return;

    var buttons = card.querySelectorAll('[data-key="' + dataKey + '"]');
    var visibleCount = 0;

    buttons.forEach(function(btn) {
      var val = btn.dataset.value;
      if (!val || val === 'all' || val === 'none' || val === 'any') {
        /* Skip/preference buttons — always visible, no count */
        btn.classList.remove('pgpm-option--unavailable');
        visibleCount++;
        return;
      }

      /* Look up the Shopify facet label for this option */
      var labelMap = FACET_LABEL_MAP[dataKey];
      var facetLabel = labelMap ? labelMap[val] : val; /* brands: val IS the label */
      var count = facetLabel && values[facetLabel] !== undefined ? values[facetLabel] : -1;

      /* Update count badge */
      var countEl = btn.querySelector('.pgpm-count');
      if (countEl) {
        if (count > 0) {
          countEl.textContent = count;
          countEl.style.display = '';
        } else if (count === 0) {
          countEl.textContent = '0';
          countEl.style.display = '';
        } else {
          countEl.textContent = '';
          countEl.style.display = 'none';
        }
      }

      /* Hide 0-result options */
      if (count === 0) {
        btn.classList.add('pgpm-option--unavailable');
      } else {
        btn.classList.remove('pgpm-option--unavailable');
        visibleCount++;
      }
    });

    /* Remove loading state */
    card.classList.remove('pgpm-card--loading');

    return visibleCount;
  }

  /* Reset all options to visible (when going back or restarting) */
  function resetCounts(step) {
    var card = root.querySelector('[data-card="' + step + '"]');
    if (!card) return;

    card.querySelectorAll('.pgpm-option--unavailable').forEach(function(btn) {
      btn.classList.remove('pgpm-option--unavailable');
    });
    card.querySelectorAll('.pgpm-count').forEach(function(el) {
      el.textContent = '';
      el.style.display = 'none';
    });
    card.classList.remove('pgpm-card--loading');
  }

  /* ══════════════════════════════════════════════════════════════
     NAVIGATION
     ══════════════════════════════════════════════════════════════ */

  function showCard(step, direction) {
    var animClass = direction === 'back' ? 'pgpm-card--slide-right' : 'pgpm-card--slide-left';

    cards.forEach(function(card) {
      card.classList.remove('pgpm-card--active', 'pgpm-card--slide-left', 'pgpm-card--slide-right');
    });

    var target = root.querySelector('[data-card="' + step + '"]');
    if (!prefersReduced) target.classList.add(animClass);
    target.classList.add('pgpm-card--active');

    dots.forEach(function(dot) {
      var dotStep = parseInt(dot.dataset.step);
      dot.classList.remove('pgpm-progress__dot--active', 'pgpm-progress__dot--done');
      if (dotStep === step) dot.classList.add('pgpm-progress__dot--active');
      else if (dotStep < step) dot.classList.add('pgpm-progress__dot--done');
    });

    var progressBar = root.querySelector('.pgpm-progress');
    if (progressBar) progressBar.setAttribute('aria-valuenow', step);

    currentStep = step;
    updateNextButton();

    var flow = root.querySelector('.pgpm-flow');
    if (flow) flow.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
  }

  /* Go to a step immediately (used for going back) */
  function goToStep(step, direction) {
    if (step < 1 || step > TOTAL_STEPS) return;

    /* Reset counts on the target step when going back */
    if (direction === 'back') resetCounts(step);

    showCard(step, direction);

    if (step === TOTAL_STEPS) buildResults();
  }

  /* Advance forward — fetches counts if the next step has a facet, then shows */
  function advanceToStep(step) {
    if (step < 1 || step > TOTAL_STEPS) return;

    /* Results step — special handling */
    if (step === TOTAL_STEPS) {
      showCard(step, 'forward');
      buildResults();
      return;
    }

    /* Does this step need facet counts? */
    if (STEP_FACET_PARAM[step]) {
      /* Show the card immediately with loading state */
      var card = root.querySelector('[data-card="' + step + '"]');
      if (card) card.classList.add('pgpm-card--loading');
      showCard(step, 'forward');

      /* Fetch counts based on all choices made so far */
      var url = buildUrl([]);
      fetchCounts(url).then(function(data) {
        lastFacets = data;
        if (data) {
          applyCounts(step, data);
        } else {
          /* Fetch failed — remove loading, show all options (graceful degradation) */
          if (card) card.classList.remove('pgpm-card--loading');
        }
      });
    } else {
      /* No counts needed — show immediately */
      showCard(step, 'forward');
    }
  }

  function updateNextButton() {
    if (!nextBtn) return;
    nextBtn.disabled = choices.brands.length === 0;
  }

  /* ══════════════════════════════════════════════════════════════
     RESULTS — Fetch full page for count + thumbnails
     ══════════════════════════════════════════════════════════════ */

  var dropLabels = {
    brands: 'brand preference', budget: 'budget range',
    purpose: 'difficulty level', artstyle: 'art style',
    pieces: 'piece count', subject: 'subject'
  };

  var RELAXATION_BASE = ['brands', 'budget', 'artstyle', 'purpose', 'pieces', 'subject'];

  function getRelaxationOrder() {
    if (choices.purpose && purposeFilters[choices.purpose] && purposeFilters[choices.purpose].length > 0
        && choices.subject && choices.subject !== 'all') {
      return ['purpose', 'brands', 'budget', 'artstyle', 'pieces', 'subject'];
    }
    return RELAXATION_BASE;
  }

  function showLoading() {
    if (titleEl) titleEl.textContent = 'Finding your puzzles\u2026';
    if (summaryEl) summaryEl.innerHTML = buildSummary();
    if (relaxedEl) { relaxedEl.textContent = ''; relaxedEl.style.display = 'none'; }
    if (previewsEl) {
      previewsEl.innerHTML =
        '<div class="pgpm-results__loading">' +
          '<div class="pgpm-results__spinner"></div>' +
          '<span class="pgpm-results__loading-text">Checking availability\u2026</span>' +
        '</div>';
    }
    if (ctaLink) ctaLink.style.display = 'none';
  }

  function showResults(url, count, thumbs, dropped) {
    if (count > 0 && titleEl) {
      titleEl.textContent = 'We found ' + count + ' puzzle' + (count !== 1 ? 's' : '') + ' for you';
    } else if (titleEl) {
      titleEl.textContent = 'We found your perfect puzzles';
    }

    /* Summary with strikethroughs on dropped filters */
    if (summaryEl) summaryEl.innerHTML = buildSummary(dropped);

    /* Explanation of what was removed */
    if (relaxedEl) {
      if (dropped.length > 0) {
        var names = dropped.map(function(k) { return dropLabels[k]; });
        var relaxMsg = 'We couldn\'t find an exact match, so we removed ';
        if (names.length === 1) {
          relaxMsg += 'the ' + names[0] + ' filter';
        } else {
          relaxMsg += names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
        }
        relaxMsg += ' to show you the closest results.';
        relaxedEl.textContent = relaxMsg;
        relaxedEl.style.display = 'block';
      } else {
        relaxedEl.textContent = '';
        relaxedEl.style.display = 'none';
      }
    }

    if (previewsEl) {
      if (thumbs.length > 0) {
        previewsEl.innerHTML = thumbs.map(function(src) {
          return '<div class="pgpm-results__thumb"><img src="' + src + '" alt="Puzzle preview" width="200" height="200" loading="lazy"></div>';
        }).join('');
      } else {
        previewsEl.innerHTML = '';
      }
    }

    if (ctaLink) {
      ctaLink.href = url;
      ctaLink.style.display = '';
    }
  }

  function buildResults() {
    showLoading();

    var activeFilters = [];
    if (choices.purpose && purposeFilters[choices.purpose] && purposeFilters[choices.purpose].length > 0) activeFilters.push('purpose');
    if (choices.subject && choices.subject !== 'all') activeFilters.push('subject');
    if (choices.artstyle && artstyleFilters[choices.artstyle]) activeFilters.push('artstyle');
    if (choices.pieces && choices.pieces !== 'any') activeFilters.push('pieces');
    if (choices.budget && budgetFilters[choices.budget]) activeFilters.push('budget');
    if (choices.brands.length > 0 && choices.brands[0] !== 'none') activeFilters.push('brands');

    if (activeFilters.length <= 1) {
      var url = buildUrl([]);
      fetchCollectionPage(url).then(function(data) {
        showResults(url, data.count, data.thumbs, []);
      });
      return;
    }

    var fullUrl = buildUrl([]);
    fetchCollectionPage(fullUrl).then(function(data) {
      if (data.count > 0 || data.count === -1) {
        showResults(fullUrl, data.count, data.thumbs, []);
        return;
      }
      tryRelax([], activeFilters);
    });
  }

  function tryRelax(dropped, activeFilters) {
    var nextDrop = null;
    var order = getRelaxationOrder();
    for (var i = 0; i < order.length; i++) {
      var candidate = order[i];
      if (activeFilters.indexOf(candidate) !== -1 && dropped.indexOf(candidate) === -1) {
        nextDrop = candidate;
        break;
      }
    }

    if (!nextDrop) {
      var fallbackUrl = '/collections/all?filter.v.availability=1';
      fetchCollectionPage(fallbackUrl).then(function(data) {
        showResults(fallbackUrl, data.count, data.thumbs, dropped);
      });
      return;
    }

    var newDropped = dropped.concat([nextDrop]);
    var url = buildUrl(newDropped);

    fetchCollectionPage(url).then(function(data) {
      if (data.count > 0 || data.count === -1) {
        showResults(url, data.count, data.thumbs, newDropped);
      } else {
        tryRelax(newDropped, activeFilters);
      }
    });
  }

  /* ══════════════════════════════════════════════════════════════
     RESET
     ══════════════════════════════════════════════════════════════ */

  function reset() {
    choices = { purpose: null, subject: null, artstyle: null, pieces: null, budget: null, brands: [] };
    lastFacets = null;

    root.querySelectorAll('.pgpm-option--selected, .pgpm-pill--selected').forEach(function(el) {
      el.classList.remove('pgpm-option--selected', 'pgpm-pill--selected');
    });

    /* Reset all step counts */
    [2, 3, 6].forEach(function(step) { resetCounts(step); });

    goToStep(1, 'back');
  }

  /* ══════════════════════════════════════════════════════════════
     EVENT HANDLING
     ══════════════════════════════════════════════════════════════ */

  var singleSelectKeys = ['purpose', 'subject', 'artstyle', 'pieces', 'budget'];

  root.addEventListener('click', function(e) {
    var target = e.target.closest('button, .pgpm-skip');
    if (!target) return;

    /* Ignore clicks on unavailable options */
    if (target.classList.contains('pgpm-option--unavailable')) return;

    if (target.classList.contains('pgpm-back')) {
      goToStep(currentStep - 1, 'back');
      return;
    }

    if (target.classList.contains('pgpm-progress__dot')) {
      var dotStep = parseInt(target.dataset.step);
      if (dotStep < currentStep) goToStep(dotStep, 'back');
      return;
    }

    if (target.id === 'pgpm-restart') {
      reset();
      return;
    }

    if (target.classList.contains('pgpm-skip')) {
      var skipKey = target.dataset.key;
      var skipValue = target.dataset.value;
      if (skipKey && skipValue) {
        choices[skipKey] = skipValue;
        advanceToStep(currentStep + 1);
      }
      return;
    }

    var key = target.dataset.key;
    var value = target.dataset.value;
    if (!key || !value) return;

    if (singleSelectKeys.indexOf(key) !== -1) {
      var card = target.closest('.pgpm-card');
      card.querySelectorAll('.pgpm-option--selected').forEach(function(el) {
        el.classList.remove('pgpm-option--selected');
      });
      target.classList.add('pgpm-option--selected');
      choices[key] = value;

      setTimeout(function() {
        advanceToStep(currentStep + 1);
      }, 250);
      return;
    }

    if (key === 'brand') {
      if (value === 'none') {
        root.querySelectorAll('.pgpm-pill--selected').forEach(function(el) {
          el.classList.remove('pgpm-pill--selected');
        });
        choices.brands = ['none'];
        setTimeout(function() {
          advanceToStep(currentStep + 1);
        }, 250);
        return;
      }

      target.classList.toggle('pgpm-pill--selected');
      choices.brands = [];
      root.querySelectorAll('.pgpm-pill--selected').forEach(function(el) {
        if (el.dataset.value && el.dataset.value !== 'none') {
          choices.brands.push(el.dataset.value);
        }
      });
      updateNextButton();
      return;
    }
  });

  if (nextBtn) {
    nextBtn.addEventListener('click', function() {
      if (choices.brands.length > 0) advanceToStep(currentStep + 1);
    });
  }

  updateNextButton();

})();
